<template>
  <div id="quarterlyReports" class="margin-subpages">
    <div class="fs-18 fw-700">{{ $t('reports.quarterly.main.report_generator') }}</div>
    <div class="line-section"></div>

    <!-- Report Configuration -->
    <div class="report-config" v-if="!isReportGenerated">
      <div class="flex-center config-row">
        <!-- Scope Selector (Team/Company) -->
        <div class="config-item">
          <label class="config-label">{{ $t('reports.quarterly.main.scope_label') }}</label>
          <el-select
            v-model="scopeSelected"
            :placeholder="$t('reports.quarterly.main.scope_placeholder')"
            class="scope-select"
            filterable
          >
            <el-option
              key="company-wide"
              :label="$t('reports.quarterly.main.company_wide')"
              value="company-wide"
            />
            <el-option
              v-for="team in teamOptions"
              :key="team.key"
              :label="team.name"
              :value="team.key"
            />
          </el-select>
        </div>

        <!-- Quarter Selector -->
        <div class="config-item">
          <label class="config-label">{{ $t('reports.quarterly.main.quarter_label') }}</label>
          <el-select
            v-model="quarterSelected"
            :placeholder="$t('reports.quarterly.main.quarter_placeholder')"
            class="quarter-select"
          >
            <el-option
              v-for="q in quarterOptions"
              :key="q.id"
              :label="q.name"
              :value="q.id"
            />
          </el-select>
        </div>

        <!-- Generate Button -->
        <div class="config-item">
          <label class="config-label">&nbsp;</label>
          <el-button
            type="primary"
            @click="generateReport"
            :loading="isLoading"
            :disabled="!canGenerate"
            class="generate-btn"
          >
            <i v-if="!isLoading" class="el-icon-document"></i>
            {{ isLoading ? $t('reports.quarterly.main.generating') : $t('reports.quarterly.main.generate_report') }}
          </el-button>
        </div>
      </div>

      <!-- Loading Progress -->
      <div v-if="isLoading" class="loading-container">
        <div class="loading-card">
          <div class="loading-header">
            <div class="loading-icon">
              <svg class="spinner" width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#E5E7EB" stroke-width="2"/>
                <path d="M12 2C6.477 2 2 6.477 2 12" stroke="#7c3aed" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="loading-title">{{ $t('reports.quarterly.main.loading_title') }}</div>
            <div class="loading-subtitle">{{ reportQuarterLabel }} · {{ reportScopeLabel }}</div>
          </div>

          <div class="progress-section">
            <el-progress
              :percentage="loadingProgress"
              :stroke-width="10"
              :color="progressColor"
              :show-text="false"
            />
            <div class="progress-info">
              <span class="progress-text">{{ loadingText }}</span>
              <span class="progress-percent">{{ loadingProgress }}%</span>
            </div>
          </div>

          <div class="loading-steps">
            <div
              v-for="(step, index) in loadingSteps"
              :key="index"
              class="step-item"
              :class="{ completed: step.completed, active: step.active }"
            >
              <div class="step-indicator">
                <i v-if="step.completed" class="el-icon-check"></i>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <span class="step-label">{{ step.label }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <div class="empty-state-icon">
          <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="60" cy="60" r="55" stroke="#E0E0E0" stroke-width="2" stroke-dasharray="8 4"/>
            <rect x="35" y="30" width="50" height="60" rx="4" fill="#F5F5F5" stroke="#E0E0E0" stroke-width="2"/>
            <rect x="42" y="40" width="36" height="4" rx="2" fill="#E0E0E0"/>
            <rect x="42" y="50" width="28" height="4" rx="2" fill="#E0E0E0"/>
            <rect x="42" y="60" width="32" height="4" rx="2" fill="#E0E0E0"/>
            <rect x="42" y="70" width="24" height="4" rx="2" fill="#E0E0E0"/>
          </svg>
        </div>
        <div class="empty-state-title">{{ $t('reports.quarterly.main.empty_title') }}</div>
        <div class="empty-state-text">
          {{ $t('reports.quarterly.main.empty_text') }}
        </div>
      </div>

      <!-- Generated Reports Section -->
      <div class="saved-reports-section">
        <div class="section-header">
          <div class="section-title">{{ $t('reports.quarterly.main.generated_reports') }}</div>
          <div class="search-container">
            <el-input
              v-model="searchQuery"
              :placeholder="$t('reports.quarterly.main.search_placeholder')"
              prefix-icon="el-icon-search"
              size="small"
              clearable
              class="search-input"
            />
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="savedReportsLoading" class="saved-reports-loading">
          <i class="el-icon-loading"></i>
          <span>{{ $t('reports.quarterly.main.loading_reports') }}</span>
        </div>

        <!-- Reports Table -->
        <el-table
          v-else-if="filteredSavedReports.length > 0"
          :data="filteredSavedReports"
          class="saved-reports-table"
          style="width: 100%"
        >
          <el-table-column prop="reportName" :label="$t('reports.quarterly.main.col_report_name')" min-width="200">
            <template slot-scope="scope">
              <span class="report-name">{{ scope.row.reportName }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="quarterName" :label="$t('reports.quarterly.main.col_quarter')" width="120" />
          <el-table-column :label="$t('reports.quarterly.main.col_created')" width="150">
            <template slot-scope="scope">
              {{ formatDate(scope.row.createdAt) }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('reports.quarterly.main.col_updated')" width="150">
            <template slot-scope="scope">
              {{ formatDate(scope.row.updatedAt) }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('reports.quarterly.main.col_actions')" width="140" align="center">
            <template slot-scope="scope">
              <div class="actions-cell">
                <el-button
                  type="primary"
                  size="mini"
                  @click="viewSavedReport(scope.row.id)"
                >
                  {{ $t('reports.quarterly.main.view') }}
                </el-button>
                <el-dropdown trigger="click" @command="handleReportAction($event, scope.row)">
                  <span class="more-btn">
                    <i class="el-icon-more"></i>
                  </span>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item command="regenerate" icon="el-icon-refresh">
                      {{ $t('reports.quarterly.main.regenerate') }}
                    </el-dropdown-item>
                    <el-dropdown-item command="delete" icon="el-icon-delete" class="delete-option">
                      {{ $t('reports.quarterly.main.delete') }}
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <!-- Empty State for No Reports -->
        <div v-else class="no-reports">
          <i class="el-icon-folder-opened"></i>
          <span v-if="searchQuery">{{ $t('reports.quarterly.main.no_search_results') }}</span>
          <span v-else>{{ $t('reports.quarterly.main.no_reports_yet') }}</span>
        </div>
      </div>
    </div>

    <!-- Slide Viewer -->
    <div v-else class="viewer-container">
      <!-- Back to Config Button -->
      <div class="viewer-header">
        <el-button
          type="text"
          @click="resetReport"
          class="back-btn"
        >
          <i class="el-icon-arrow-left"></i>
          {{ $t('reports.quarterly.main.back_to_config') }}
        </el-button>
        <div class="report-info">
          <span class="report-quarter">{{ reportQuarterName }}</span>
          <span class="report-separator">|</span>
          <span class="report-team">{{ reportTeamName }}</span>
        </div>
      </div>

      <!-- Slide Viewer Component -->
      <quarterly-slide-viewer
        ref="slideViewer"
        :slides="slides"
        :current-index="currentSlideIndex"
        :is-fullscreen="isFullscreen"
        :is-generating-pdf="isGeneratingPdf"
        :is-pdf-capturing="isPdfCapturing"
        @navigate="handleNavigate"
        @fullscreen="toggleFullscreen"
        @download-pdf="downloadAsPdf"
      />

      <div
        v-if="pdfSlideData"
        ref="pdfExportSurface"
        class="pdf-export-surface"
        aria-hidden="true"
      >
        <div class="slide-viewport pdf-capture-mode pdf-export-viewport">
          <quarterly-slide-factory
            :key="pdfSlideData.id"
            :slide-data="pdfSlideData"
          />
        </div>
      </div>

      <!-- PDF Generation Overlay -->
      <div v-if="isGeneratingPdf" class="pdf-overlay">
        <div class="pdf-overlay-content">
          <!-- Icon -->
          <div class="pdf-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M14 2V8H20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12 18V12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M9 15L12 12L15 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>

          <!-- Status Text -->
          <h3 class="pdf-title">{{ $t('reports.quarterly.main.pdf_title') }}</h3>
          <p class="pdf-subtitle">{{ reportTeamName }} · {{ reportQuarterName }}</p>

          <!-- Progress Bar -->
          <div class="pdf-progress-container">
            <div class="pdf-progress-bar">
              <div
                class="pdf-progress-fill"
                :style="{ width: `${(pdfProgress / slides.length) * 100}%` }"
              ></div>
            </div>
            <div class="pdf-progress-info">
              <span class="pdf-progress-text">Processing slide {{ pdfProgress }} of {{ slides.length }}</span>
              <span class="pdf-progress-percent">{{ Math.round((pdfProgress / slides.length) * 100) }}%</span>
            </div>
          </div>

          <!-- Tip -->
          <p class="pdf-tip">{{ $t('reports.quarterly.main.pdf_tip') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QuarterlySlideFactory from './quarterly/QuarterlySlideFactory.vue';
import { preloadQuarterlySlideComponents } from './quarterly/slideRegistry';
import {
  waitForAnimationFrames,
  waitForDocumentFonts,
  waitForImages,
  waitForSlideRender
} from './quarterly/pdfExportUtils';

export default {
  name: 'QuarterlyReport',
  components: {
    'quarterly-slide-viewer': () => import('./quarterly/QuarterlySlideViewer.vue'),
    'quarterly-slide-factory': QuarterlySlideFactory
  },
  created() {
    // Initialize quarter options from existing report store
    this.$store.dispatch('report/getQuarter');
    this.$store.dispatch('report/getKeys');
    this.$store.dispatch('report/getQuestionMaster');
    this.$store.dispatch('team/getTeamsOptions', this.user);
    this.$store.dispatch('quarterlyReport/initQuarterOptions');
    // Fetch saved reports
    this.$store.dispatch('quarterlyReport/fetchSavedReports');
  },
  computed: {
    user() {
      return this.$store.getters['authen/user'] || {};
    },
    teamOptions() {
      return this.$store.getters['team/teamsall'] || [];
    },
    currentQuarterId() {
      // Calculate current quarter ID (e.g., "20261" for Q1 2026)
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1; // 1-12
      const quarter = Math.ceil(month / 3);
      return `${year}${quarter}`;
    },
    quarterOptions() {
      // Get quarters from report store, excluding "MOST RECENT SCORES" and current incomplete quarter
      const options = this.$store.getters['report/quarterOption'] || [];
      return options.filter(q => {
        if (q.type === 'recent') return false;
        // Exclude current quarter as it has incomplete data
        if (q.id === this.currentQuarterId) return false;
        return true;
      });
    },
    isLoading() {
      return this.$store.getters['quarterlyReport/isLoading'];
    },
    loadingProgress() {
      return this.$store.getters['quarterlyReport/loadingProgress'];
    },
    isReportGenerated() {
      return this.$store.getters['quarterlyReport/isReportGenerated'];
    },
    slides() {
      return this.$store.getters['quarterlyReport/slides'];
    },
    currentSlideIndex() {
      return this.$store.getters['quarterlyReport/currentSlideIndex'];
    },
    isFullscreen() {
      return this.$store.getters['quarterlyReport/isFullscreen'];
    },
    reportData() {
      return this.$store.getters['quarterlyReport/reportData'];
    },
    reportQuarterName() {
      return (this.reportData && this.reportData.quarter && this.reportData.quarter.name) || '';
    },
    reportTeamName() {
      return (this.reportData && this.reportData.teamName) || '';
    },
    canGenerate() {
      return this.scopeSelected && this.quarterSelected;
    },
    loadingText() {
      const progress = this.loadingProgress;
      if (progress < 10) return 'Initializing...';
      if (progress < 30) return 'Fetching core metrics...';
      if (progress < 50) return 'Analyzing question scores...';
      if (progress < 60) return 'Identifying highlights...';
      if (progress < 70) return 'Analyzing manager effectiveness...';
      if (progress < 80) return 'Finding culture leaders...';
      if (progress < 100) return 'Generating slides...';
      return 'Complete!';
    },
    progressColor() {
      return '#7c3aed'; // violet-500
    },
    reportQuarterLabel() {
      const q = this.quarterOptions.find(q => q.id === this.quarterSelected);
      return q ? q.name : 'Quarter';
    },
    reportScopeLabel() {
      if (this.scopeSelected === 'company-wide') return 'Company-wide';
      const team = this.teamOptions.find(t => t.key === this.scopeSelected);
      return team ? team.name : 'Team';
    },
    loadingSteps() {
      const progress = this.loadingProgress;
      return [
        { label: 'Core Metrics', completed: progress >= 30, active: progress > 0 && progress < 30 },
        { label: 'Question Analysis', completed: progress >= 50, active: progress >= 30 && progress < 50 },
        { label: 'Highlights', completed: progress >= 60, active: progress >= 50 && progress < 60 },
        { label: 'Manager Data', completed: progress >= 70, active: progress >= 60 && progress < 70 },
        { label: 'Culture Leaders', completed: progress >= 80, active: progress >= 70 && progress < 80 },
        { label: 'Build Slides', completed: progress >= 100, active: progress >= 80 && progress < 100 }
      ];
    },
    // Saved reports
    savedReports() {
      return this.$store.getters['quarterlyReport/savedReports'] || [];
    },
    savedReportsLoading() {
      return this.$store.getters['quarterlyReport/savedReportsLoading'];
    },
    filteredSavedReports() {
      if (!this.searchQuery) return this.savedReports;
      const query = this.searchQuery.toLowerCase();
      return this.savedReports.filter(report =>
        report.reportName.toLowerCase().includes(query) ||
        report.quarterName.toLowerCase().includes(query) ||
        report.teamName.toLowerCase().includes(query)
      );
    }
  },
  data() {
    return {
      scopeSelected: 'company-wide',
      quarterSelected: '',
      searchQuery: '',
      isGeneratingPdf: false,
      isPdfCapturing: false,
      pdfProgress: 0,
      pdfSlideData: null
    };
  },
  mounted() {
    // Set default quarter to most recent complete quarter
    if (this.quarterOptions.length > 0) {
      this.quarterSelected = (this.quarterOptions[0] && this.quarterOptions[0].id) || '';
    }
  },
  watch: {
    quarterOptions(newVal) {
      if (newVal.length > 0 && !this.quarterSelected) {
        this.quarterSelected = (newVal[0] && newVal[0].id) || '';
      }
    }
  },
  methods: {
    generateReport() {
      if (!this.canGenerate) return;

      const selectedQuarterObj = this.quarterOptions.find(q => q.id === this.quarterSelected);
      if (!selectedQuarterObj) return;

      // Parse quarter info
      const year = parseInt(selectedQuarterObj.id.substring(0, 4));
      const quarter = parseInt(selectedQuarterObj.id.substring(4));

      this.$store.dispatch('quarterlyReport/generateQuarterlyReport', {
        quarter: {
          id: selectedQuarterObj.id,
          name: selectedQuarterObj.name,
          year: year,
          quarter: quarter
        },
        team: this.scopeSelected
      });
    },
    resetReport() {
      this.$store.commit('quarterlyReport/resetReport');
    },
    handleNavigate(direction) {
      if (direction === 'next') {
        this.$store.dispatch('quarterlyReport/nextSlide');
      } else if (direction === 'prev') {
        this.$store.dispatch('quarterlyReport/prevSlide');
      } else if (typeof direction === 'number') {
        this.$store.dispatch('quarterlyReport/goToSlide', direction);
      }
    },
    toggleFullscreen() {
      this.$store.dispatch('quarterlyReport/toggleFullscreen');
    },
    viewSavedReport(reportId) {
      this.$store.dispatch('quarterlyReport/loadSavedReport', reportId);
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },
    handleReportAction(command, report) {
      if (command === 'regenerate') {
        this.regenerateReport(report);
      } else if (command === 'delete') {
        this.confirmDeleteReport(report);
      }
    },
    regenerateReport(report) {
      // Set the selectors to match the saved report
      this.scopeSelected = report.teamId;
      this.quarterSelected = report.quarterId;

      // Trigger generation
      this.$nextTick(() => {
        this.generateReport();
      });
    },
    confirmDeleteReport(report) {
      this.$confirm(
        `Are you sure you want to delete "${report.reportName}"? This action cannot be undone.`,
        'Delete Report',
        {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning',
          confirmButtonClass: 'el-button--danger'
        }
      ).then(() => {
        this.deleteReport(report.id);
      }).catch(() => {
        // User cancelled
      });
    },
    async deleteReport(reportId) {
      await this.$store.dispatch('quarterlyReport/deleteReport', reportId);
      this.$message({
        type: 'success',
        message: 'Report deleted successfully'
      });
    },
    async imageToDataUrl(imgSrc) {
      try {
        const response = await fetch(imgSrc, { mode: 'cors' });
        const blob = await response.blob();
        return await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        });
      } catch {
        return null;
      }
    },
    getPdfAvatarInitials(name) {
      if (!name) return '?';

      const parts = name.trim().split(/\s+/);
      if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase();
      }

      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },
    getPdfAvatarColor(name) {
      const colors = [
        '#7c3aed',
        '#059669',
        '#d97706',
        '#0284c7',
        '#e11d48',
        '#7c3aed',
        '#8b5cf6',
        '#06b6d4'
      ];

      let hash = 0;
      for (let i = 0; i < (name || '').length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }

      return colors[Math.abs(hash) % colors.length];
    },
    async hydratePdfImages(rootEl) {
      const images = Array.from(rootEl.querySelectorAll('img'));
      const imageDataUrls = new Map();

      await Promise.all(images.map(async (img) => {
        if (img.src && img.src.startsWith('http')) {
          const dataUrl = await this.imageToDataUrl(img.src);
          if (dataUrl) {
            imageDataUrls.set(img.src, dataUrl);
          }
        }
      }));

      images.forEach((img) => {
        const dataUrl = imageDataUrls.get(img.src);
        if (dataUrl) {
          img.src = dataUrl;
          return;
        }

        if (!img.src || !img.src.startsWith('http')) {
          return;
        }

        img.style.display = 'none';
        const parent = img.parentElement;
        if (!parent) return;

        const leaderRow = parent.closest('.leader-row');
        const nameEl = leaderRow ? leaderRow.querySelector('.leader-name') : null;
        const personName = nameEl ? nameEl.textContent : '';

        if (
          (parent.classList.contains('leader-avatar') || parent.classList.contains('avatar')) &&
          !parent.querySelector('[data-pdf-avatar-fallback="true"]')
        ) {
          const initials = document.createElement('div');
          initials.setAttribute('data-pdf-avatar-fallback', 'true');
          initials.textContent = this.getPdfAvatarInitials(personName);
          initials.style.cssText = `
            width: 100%;
            height: 100%;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'DM Sans', 'Kanit', sans-serif;
            font-size: 28px;
            font-weight: 600;
            color: #fff;
            background: ${this.getPdfAvatarColor(personName)};
            box-sizing: border-box;
          `;
          parent.appendChild(initials);
        }
      });

      await waitForImages(rootEl);
    },
    async renderSlideOnPdfSurface(slide) {
      this.pdfSlideData = slide;
      await this.$nextTick();

      const pdfSurface = this.$refs.pdfExportSurface;
      const exportViewport = pdfSurface && pdfSurface.querySelector('.pdf-export-viewport');

      if (!pdfSurface || !exportViewport) {
        throw new Error('PDF export surface not found');
      }

      await waitForSlideRender(exportViewport, slide.id);
      await waitForDocumentFonts(document);
      await waitForImages(exportViewport);
      await waitForAnimationFrames(2);

      await this.hydratePdfImages(exportViewport);
      await waitForDocumentFonts(document);
      await waitForImages(exportViewport);
      await waitForAnimationFrames(2);

      return exportViewport;
    },
    async downloadAsPdf() {
      if (this.isGeneratingPdf || this.slides.length === 0) return;

      this.isGeneratingPdf = true;
      this.pdfProgress = 0;

      try {
        await preloadQuarterlySlideComponents(this.slides.map(slide => slide.type));

        // Dynamically import html2canvas and jsPDF
        const html2canvas = (await import('html2canvas')).default;
        const jsPDFModule = await import('jspdf');
        const jsPDF = jsPDFModule.jsPDF || jsPDFModule.default;

        // Generate filename
        const teamName = this.reportData.teamName || 'Report';
        const quarter = this.reportData.quarter || {};
        const filename = `${teamName}_Q${quarter.quarter || ''}_${quarter.year || ''}.pdf`;

        // Create PDF with landscape 16:9 dimensions
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'px',
          format: [1920, 1080],
          hotfixes: ['px_scaling']
        });

        for (let i = 0; i < this.slides.length; i++) {
          const slide = this.slides[i];
          this.pdfProgress = i + 1;
          const exportViewport = await this.renderSlideOnPdfSurface(slide);

          // Capture the cloned slide at full resolution with 2x scale for crisp output
          const canvas = await html2canvas(exportViewport, {
            width: 1920,
            height: 1080,
            scale: 2, // 2x scale for high DPI / crisp text
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: null,
            onclone: (clonedDoc) => {
              // Ensure pdf-capture-mode styles are applied in cloned document
              const clonedSlide = clonedDoc.querySelector('.pdf-capture-mode');
              if (clonedSlide) {
                // Force all children to be visible
                clonedSlide.querySelectorAll('*').forEach(el => {
                  el.style.opacity = '1';
                  el.style.visibility = 'visible';
                  el.style.animation = 'none';
                  el.style.transition = 'none';
                });

                // Fix gradient text - replace with solid colors
                clonedSlide.querySelectorAll('.title, .hero-metric').forEach(el => {
                  el.style.background = 'none';
                  el.style.webkitBackgroundClip = 'unset';
                  el.style.backgroundClip = 'unset';
                  el.style.webkitTextFillColor = 'unset';
                  // Set appropriate color based on context
                  if (el.classList.contains('title')) {
                    el.style.color = '#ffffff';
                  } else if (el.classList.contains('hero-metric')) {
                    if (el.classList.contains('score-high')) {
                      el.style.color = '#059669';
                    } else if (el.classList.contains('score-medium')) {
                      el.style.color = '#d97706';
                    } else if (el.classList.contains('score-low')) {
                      el.style.color = '#e11d48';
                    }
                  }
                });
              }
            }
          });

          // Add new page for slides after the first
          if (i > 0) {
            pdf.addPage([1920, 1080], 'landscape');
          }

          // Add the canvas as PNG image to PDF (better quality than JPEG for graphics)
          const imgData = canvas.toDataURL('image/png');
          pdf.addImage(imgData, 'PNG', 0, 0, 1920, 1080, undefined, 'FAST');
        }

        // Save the PDF
        pdf.save(filename);

        this.$message.success('PDF downloaded successfully');
      } catch (error) {
        console.error('PDF generation failed:', error);
        this.$message.error('Failed to generate PDF. Please try again.');
      } finally {
        this.pdfSlideData = null;
        await this.$nextTick();
        this.isPdfCapturing = false;
        this.isGeneratingPdf = false;
        this.pdfProgress = 0;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
#quarterlyReports {
  .line-section {
    margin: 20px 0;
    width: 100%;
    height: 1px;
    background-color: #e0e0e0;
  }

  .report-config {
    .config-row {
      gap: 24px;
      flex-wrap: wrap;
    }

    .config-item {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .config-label {
      font-size: 12px;
      font-weight: 600;
      color: #6b6b6b;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .scope-select,
    .quarter-select {
      width: 240px;
    }

    .generate-btn {
      min-width: 180px;
      height: 40px;
      background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
      border: none;
      font-weight: 600;

      &:hover:not(:disabled) {
        background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
      }

      &:disabled {
        background: #e0e0e0;
        color: #9e9e9e;
      }

      i {
        margin-right: 8px;
      }
    }
  }

  .loading-container {
    display: flex;
    justify-content: center;
    margin-top: 48px;

    .loading-card {
      background: #fff;
      border-radius: 16px;
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
      padding: 40px 48px;
      width: 100%;
      max-width: 520px;
    }

    .loading-header {
      text-align: center;
      margin-bottom: 32px;
    }

    .loading-icon {
      margin-bottom: 16px;

      .spinner {
        animation: spin 1s linear infinite;
      }
    }

    .loading-title {
      font-size: 20px;
      font-weight: 700;
      color: #1f2937;
      margin-bottom: 8px;
    }

    .loading-subtitle {
      font-size: 14px;
      color: #6b7280;
    }

    .progress-section {
      margin-bottom: 32px;
    }

    .progress-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 12px;
    }

    .progress-text {
      font-size: 14px;
      color: #6b7280;
    }

    .progress-percent {
      font-size: 14px;
      font-weight: 600;
      color: #7c3aed;
    }

    .loading-steps {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
    }

    .step-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      opacity: 0.4;
      transition: all 0.3s ease;

      &.completed {
        opacity: 1;

        .step-indicator {
          background: #7c3aed;
          color: #fff;
          border-color: #7c3aed;
        }
      }

      &.active {
        opacity: 1;

        .step-indicator {
          border-color: #7c3aed;
          color: #7c3aed;
          animation: pulse 1.5s ease-in-out infinite;
        }
      }
    }

    .step-indicator {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: 2px solid #e5e7eb;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: 600;
      color: #9ca3af;
      background: #fff;
      transition: all 0.3s ease;
    }

    .step-label {
      font-size: 12px;
      color: #6b7280;
      text-align: center;
    }
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 24px;
    text-align: center;

    .empty-state-icon {
      margin-bottom: 24px;
      opacity: 0.8;
    }

    .empty-state-title {
      font-size: 20px;
      font-weight: 700;
      color: #3d3d3d;
      margin-bottom: 12px;
    }

    .empty-state-text {
      font-size: 14px;
      color: #6b6b6b;
      max-width: 400px;
      line-height: 1.6;
    }
  }

  .viewer-container {
    position: relative;

    .viewer-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 24px;
      padding-bottom: 16px;
      border-bottom: 1px solid #e0e0e0;

      .back-btn {
        color: #6b6b6b;
        font-weight: 500;

        &:hover {
          color: #7c3aed;
        }

        i {
          margin-right: 8px;
        }
      }

      .report-info {
        display: flex;
        align-items: center;
        gap: 12px;
        font-size: 14px;

        .report-quarter {
          font-weight: 700;
          color: #3d3d3d;
        }

        .report-separator {
          color: #e0e0e0;
        }

        .report-team {
          color: #6b6b6b;
        }
      }
    }

    .pdf-export-surface {
      position: fixed;
      left: -20000px;
      top: 0;
      width: 1920px;
      height: 1080px;
      overflow: hidden;
      pointer-events: none;
      z-index: -1;
      background: #fff;
    }

    .pdf-export-viewport {
      width: 1920px;
      height: 1080px;
      position: relative;
      transform: none;
      transform-origin: top left;
    }
  }

  // Generated Reports Section
  .saved-reports-section {
    margin-top: 48px;
    padding-top: 32px;
    border-top: 1px solid #e0e0e0;

    .section-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 24px;
      flex-wrap: wrap;
      gap: 16px;
    }

    .section-title {
      font-size: 18px;
      font-weight: 700;
      color: #3d3d3d;
    }

    .search-container {
      .search-input {
        width: 280px;
      }
    }

    .saved-reports-loading {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 48px;
      color: #6b6b6b;

      i {
        font-size: 20px;
        color: #7c3aed;
      }
    }

    .saved-reports-table {
      border-radius: 8px;
      overflow: hidden;

      .report-name {
        font-weight: 600;
        color: #3d3d3d;
      }

      .actions-cell {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
      }

      .more-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 4px;
        cursor: pointer;
        color: #6b6b6b;
        transition: all 0.2s ease;

        &:hover {
          background-color: #f0f0f0;
          color: #3d3d3d;
        }

        i {
          transform: rotate(90deg);
          font-size: 14px;
        }
      }
    }

    .no-reports {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 48px;
      color: #9e9e9e;
      text-align: center;

      i {
        font-size: 32px;
      }
    }
  }

  // PDF Generation Overlay
  .pdf-overlay {
    position: fixed;
    inset: 0;
    background: #0f0f0f; // Fully opaque dark background
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;

    .pdf-overlay-content {
      text-align: center;
      color: white;
      max-width: 400px;
      padding: 40px;
    }

    .pdf-icon {
      margin-bottom: 24px;
      color: #7c3aed;
      animation: pulse-icon 2s ease-in-out infinite;

      svg {
        filter: drop-shadow(0 0 20px rgba(124, 58, 237, 0.4));
      }
    }

    .pdf-title {
      font-size: 24px;
      font-weight: 700;
      margin: 0 0 8px;
      color: #fff;
    }

    .pdf-subtitle {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.6);
      margin: 0 0 32px;
    }

    .pdf-progress-container {
      width: 100%;
      margin-bottom: 24px;
    }

    .pdf-progress-bar {
      width: 100%;
      height: 8px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 4px;
      overflow: hidden;
      margin-bottom: 12px;
    }

    .pdf-progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #7c3aed 0%, #a78bfa 100%);
      border-radius: 4px;
      transition: width 0.3s ease;
    }

    .pdf-progress-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .pdf-progress-text {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.7);
    }

    .pdf-progress-percent {
      font-size: 14px;
      font-weight: 600;
      color: #a78bfa;
    }

    .pdf-tip {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.4);
      margin: 0;
    }
  }

  @keyframes pulse-icon {
    0%, 100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.05);
      opacity: 0.8;
    }
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
}
</style>

<style lang="scss">
// Non-scoped styles for Element UI dropdown (renders outside component)
.el-dropdown-menu {
  .delete-option {
    color: #f56c6c;

    &:hover {
      background-color: #fef0f0;
      color: #f56c6c;
    }

    i {
      color: #f56c6c;
    }
  }
}
</style>
